import { useEffect, useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useTranslation } from "@/i18n"
import { useSearch, type SearchTab } from "@/hooks/useSearch"
import { usePostFilters } from "@/hooks/usePostFilters"
import { useFilteredPosts } from "@/hooks/useFilteredPosts"
import { useSettings } from "@/state/SettingsContext"
import { Tabs } from "@/components/Tabs"
import { NoteCard } from "@/components/NoteCard"
import { PostTypeFilter } from "@/components/PostTypeFilter"
import { PostFilters } from "@/components/filters/PostFilters"
import { RelayCard } from "@/components/RelayCard"
import { Avatar } from "@/components/Avatar"
import { UserName } from "@/components/UserName"
import { EmptyState, Skeleton } from "@/components/Feedback"
import { Icon } from "@/components/Icon"
import { postTypeSelection, relayFilterHints } from "@/nostr/api/filters/filterEngine"
import { simplifyUrl } from "@/utils/url"

/** Search page: relay-backed (NIP-50) note/profile search and a relay index. */
export function SearchPage() {
  const { t } = useTranslation()
  const [params, setParams] = useSearchParams()
  const [query, setQuery] = useState(params.get("q") ?? "")
  const [tab, setTab] = useState<SearchTab>("notes")
  const postFilters = usePostFilters("search")
  const filters = postFilters.filters
  const { settings } = useSettings()
  const hints = useMemo(() => relayFilterHints(filters), [filters])
  const results = useSearch(query, tab, filters.postTypes, {
    since: hints.since,
    until: hints.until,
  })
  // Time bounds and post types are already applied by the subscription; the
  // remaining filters (media, language, engagement, authors) run client side.
  const visible = useFilteredPosts(results.notes, filters, {
    mutedWords: settings.mutedWords,
    enabled: tab === "notes",
  })

  useEffect(() => {
    const next = new URLSearchParams(params)
    if (query.trim()) next.set("q", query.trim())
    else next.delete("q")
    setParams(next, { replace: true })
    // only the query should update the URL
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  return (
    <div className="page search-page">
      <header className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">{t("Search")}</h1>
          <p className="page-subtitle">
            {results.mode === "relay"
              ? `${t("Relay search")} · ${results.searchedRelays.map(simplifyUrl).join(", ") || "NIP-50"}`
              : results.mode === "local"
                ? t("Local filter")
                : t("Search notes and people")}
          </p>
        </div>
      </header>

      <div className="search-input">
        <Icon name="search" size={18} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("Search notes and people")}
          autoFocus
          aria-label={t("Search notes and people")}
          spellCheck={false}
        />
        {query && (
          <button type="button" className="icon-button small" onClick={() => setQuery("")} aria-label="Clear">
            <Icon name="x" size={14} />
          </button>
        )}
      </div>

      {results.mode === "local" && query && (
        <p className="muted small search-note">
          No NIP-50 search relay is configured, so this query filters recent events client side. Add a search
          relay in <Link to="/settings">settings</Link> for relay-backed search.
        </p>
      )}

      <Tabs
        value={tab}
        tabs={[
          { value: "notes", label: t("Notes") },
          { value: "profiles", label: t("People") },
          { value: "relays", label: t("Relays") },
        ]}
        onChange={(value) => setTab(value as SearchTab)}
        ariaLabel={t("Search")}
      />

      {!query.trim() && (
        <EmptyState
          icon="search"
          title={t("Search the Nostr network")}
          description={t("Type a word, a hashtag or a relay address to start.")}
        />
      )}

      {query.trim() && results.loading && <Skeleton lines={3} />}

      {query.trim() && tab === "notes" && (
        <div className="filter-bar">
          <PostTypeFilter
            value={postTypeSelection(filters)}
            onChange={(selection) =>
              postFilters.patch({ postTypes: selection === "all" ? [] : [selection] })
            }
          />
          <PostFilters
            value={filters}
            active={postFilters.active}
            onApply={(next) => postFilters.patch(next)}
            onClearAll={postFilters.clearAll}
            onRemove={postFilters.remove}
            categories={["content", "time"]}
            mutedWordCount={settings.mutedWords.length}
          />
        </div>
      )}

      {query.trim() && tab === "notes" && visible.hidden > 0 && (
        <p className="muted small filter-hidden-note">
          {t("{n} posts hidden by the active filters", { n: visible.hidden })}
        </p>
      )}

      {query.trim() && !results.loading && tab === "notes" && (
        <>
          {visible.events.length === 0 && (
            <EmptyState
              icon="search"
              title={postFilters.isDefault ? t("No results") : t("No posts match the filters")}
              description={
                postFilters.isDefault
                  ? t("Try a different search term.")
                  : t("No post matched the selected filters. Relax a filter or clear them all.")
              }
            />
          )}
          {visible.events.map((note) => (
            <NoteCard key={note.id} event={note} />
          ))}
        </>
      )}

      {query.trim() && !results.loading && tab === "profiles" && (
        <>
          {results.profiles.length === 0 && (
            <EmptyState icon="users" title={t("No people found")} description={t("Try another term.")} />
          )}
          <ul className="profile-result-list">
            {results.profiles.map((profile) => (
              <li key={profile.pubkey} className="profile-result">
                <Avatar src={profile.picture} label={profile.displayName || profile.pubkey} size={40} />
                <div>
                  <UserName pubkey={profile.pubkey} profile={profile} showHandle />
                  {profile.about && <p className="muted small">{profile.about.slice(0, 160)}</p>}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {query.trim() && tab === "relays" && (
        <>
          {!results.loading && results.relays.length === 0 && (
            <EmptyState
              icon="relay"
              title={t("No relays found")}
              description={t("Add it manually in settings if you know the address.")}
            />
          )}
          <div className="relay-grid">
            {results.relays.map((info) => (
              <RelayCard key={info.url} url={info.url} info={info} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}
